import display from 'assets/campaign/adType-display.png';
import display2x from 'assets/campaign/adType-display@2x.png';
import video from 'assets/campaign/adType-video.png';
import video2x from 'assets/campaign/adType-video@2x.png';
import outdoor from 'assets/campaign/adType-outdoor.png';
import outdoor2x from 'assets/campaign/adType-outdoor@2x.png';
import thirdParty from 'assets/campaign/adType-thirdParty.png';
import thirdParty2x from 'assets/campaign/adType-thirdParty@2x.png';
import { AdType } from 'core/rtbCampaign/RtbCampaign';
import {
  DefaultCampaignAdTypeButtonModel,
  CampaignAdTypeButtonModel
} from 'components/CampaignAdTypeButton/CampaignAdTypeButtonModel';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { AddonFeatureType } from 'core/agency/AddonFeature';
import { RtbCampaignSetupFlowPageModel } from '../RtbCampaignSetupFlowPageModel';
import i18n from 'i18next';
import _ from 'lodash';
import { L1ObjectObjective } from 'core/l1Object/L1Object';
import {
  CampaignAdTypeSetting,
  ChannelGroupSetting,
  StaticAdTypeSetting
} from 'core/channelGroup/ChannelGroupSettings';
import { CampaignAdTypeState } from 'core/channelGroup/ChannelGroup';

export interface ChooseAdTypeStepModel {
  readonly event: UpdateEventListener<ChooseAdTypeStepModel>;
  readonly state: ChooseAdTypeStepState;
  readonly flowModel: RtbCampaignSetupFlowPageModel;
  readonly getAdTypeButtonModels: () => CampaignAdTypeButtonModel[];
  readonly showChangeAdTypeAlertModal: (
    confirmFuc: () => Promise<void>
  ) => void;
  onAdTypeChange: (adType: AdType, adTypeId?: number) => void;
  backToL1ObjectDetail: () => void;
}

export type ChooseAdTypeStepProps = {
  readonly model: ChooseAdTypeStepModel;
};

export type ChooseAdTypeStepState = {
  readonly changeAdTypeAlertModal?: any;
};

export class DefaultChooseAdTypeStepModel implements ChooseAdTypeStepModel {
  event: FireableUpdateEventListener<ChooseAdTypeStepModel>;
  adType?: string;
  adTypeId?: number;
  changeAdTypeAlertModal?: any;
  allAdTypeSettings: CampaignAdTypeSetting[];
  supportPMP: boolean;

  constructor(
    public flowModel: RtbCampaignSetupFlowPageModel,
    private goNext: () => void
  ) {
    this.adType = flowModel.state.campaign!.basic.adType;
    this.adTypeId = flowModel.state.campaign!.basic.adTypeId;
    this.event = new FireableUpdateEventListener<ChooseAdTypeStepModel>();
    const channelGroupSetting = ChannelGroupSetting.fromKey(
      flowModel.l1Object.channel
    );
    const channelGroupConfig = channelGroupSetting.config;
    this.allAdTypeSettings =
      channelGroupSetting.getActiveCampaignAdTypes() || [];
    this.supportPMP =
      channelGroupConfig.addonFeatures?.campaign.supportPMP || false;
  }

  get state() {
    return {
      changeAdTypeAlertModal: this.changeAdTypeAlertModal
    };
  }

  get adTypeSettings() {
    const addonFeatureManager = this.flowModel.addonFeatureManager;
    const objective = _.get(this.flowModel.l1Object, 'objective');
    const objectiveDependancy = {
      [AdType.CTV]: [L1ObjectObjective.AWARENESS]
    };
    const allowedAdType = this.allAdTypeSettings.reduce(
      (allowAdType, setting) => {
        const adType = setting.name;
        // ad type add-on 的 key：有 adTypeId 用 adTypeId，否則用 name
        const adTypeOptionKey = setting.adTypeId ?? setting.name;

        // 直接看該 ad type 的 add-on 是否開啟
        const hasPermission =
          adType !== AdType.ONE_FOR_ALL_DISPLAY &&
          addonFeatureManager.isFeatureEnable(
            `adType_${adTypeOptionKey}` as AddonFeatureType
          );
        const objectiveCompatible =
          !objectiveDependancy[adType] ||
          objectiveDependancy[adType].includes(objective);
        if (hasPermission && objectiveCompatible) {
          allowAdType.push(adType);
        }
        return allowAdType;
      },
      [] as Array<string>
    );

    const allowedAdTypeSettings = _.compact(
      allowedAdType.map((adType) =>
        this.allAdTypeSettings.find((setting) => setting.name === adType)
      )
    );
    let pmpAdTypeSetting: StaticAdTypeSetting | undefined = undefined;
    if (allowedAdTypeSettings.length > 0) {
      const hasPmpOrder = this.flowModel.pmpList.length > 0;
      if (this.supportPMP && hasPmpOrder) {
        pmpAdTypeSetting = {
          name: 'PMP',
          channelGroupId: this.allAdTypeSettings[0].channelGroupId,
          state: CampaignAdTypeState.ACTIVE,
          bannerTypes: allowedAdTypeSettings.flatMap(
            (setting) => setting.bannerTypes
          )
        };

        return [...allowedAdTypeSettings, pmpAdTypeSetting];
      }
    }
    return allowedAdTypeSettings;
  }

  getAdTypeButtonModels = () => {
    return this.adTypeSettings.map((setting) => {
      const selected = setting.adTypeId
        ? setting.adTypeId === this.adTypeId
        : setting.name === this.adType;
      return new DefaultCampaignAdTypeButtonModel(
        this.getAdTypeButtonData(setting),
        selected,
        _.partial(this.onAdTypeButtonClick, setting.name, setting.adTypeId)
      );
    });
  };

  onAdTypeButtonClick = (adType: string, adTypeId?: number) => {
    const campaign = this.flowModel.state.campaign;
    const currentAdType = _.get(campaign, 'basic.adType');
    const currentAdTypeId = _.get(campaign, 'basic.adTypeId');
    if (
      currentAdType !== undefined &&
      (adType !== currentAdType || adTypeId !== currentAdTypeId)
    ) {
      this.showChangeAdTypeAlertModal(async () => {
        await this.onAdTypeChange(adType, adTypeId);
      });
    } else {
      this.onAdTypeChange(adType, adTypeId);
    }
  };

  async onAdTypeChange(adType: string, adTypeId?: number) {
    this.adType = adType;
    this.adTypeId = adTypeId;
    await this.flowModel.onAdTypeChange(adType, adTypeId);
    this.goNext();
  }

  getDefaultOutdoorAdTypeButtonData(adType: string) {
    let titleI18nKey = `campaign.buttons.adType${_.upperFirst(_.camelCase(adType))}Title`;
    let descI18nKey = `campaign.buttons.adType${_.upperFirst(_.camelCase(adType))}Desc`;
    return {
      icon: outdoor,
      icon2x: outdoor2x,
      title: i18n.exists(titleI18nKey)
        ? `campaign.buttons.adType${_.upperFirst(_.camelCase(adType))}Title`
        : adType,
      description: i18n.exists(descI18nKey)
        ? `campaign.buttons.adType${_.upperFirst(_.camelCase(adType))}Desc`
        : adType
    };
  }

  getAdTypeButtonData(adTypeSetting: CampaignAdTypeSetting) {
    if (adTypeSetting.adTypeId) {
      return {
        icon: outdoor,
        icon2x: outdoor2x,
        title: adTypeSetting.name,
        description: ''
      };
    }

    switch (adTypeSetting.name) {
      case AdType.DISPLAY:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeDisplayTitle',
          description: 'campaign.buttons.adTypeDisplayDesc'
        };
      case AdType.THIRD_PARTY:
        return {
          icon: thirdParty,
          icon2x: thirdParty2x,
          title: 'campaign.buttons.adTypeThirdPartyTitle',
          description: 'campaign.buttons.adTypeThirdPartyDesc'
        };
      case AdType.THIRD_PARTY_BOTTOM:
        return {
          icon: thirdParty,
          icon2x: thirdParty2x,
          title: 'campaign.buttons.adTypeThirdPartyBottomTitle',
          description: 'campaign.buttons.adTypeThirdPartyBottomDesc'
        };
      case AdType.THIRD_PARTY_RECTANGLE:
        return {
          icon: thirdParty,
          icon2x: thirdParty2x,
          title: 'campaign.buttons.adTypeThirdPartyRectangleTitle',
          description: 'campaign.buttons.adTypeThirdPartyRectangleDesc'
        };
      case AdType.VIDEO:
        return {
          icon: video,
          icon2x: video2x,
          title: 'campaign.buttons.adTypeVideoTitle',
          description: 'campaign.buttons.adTypeVideoDesc'
        };
      case AdType.COMBO:
        return {
          icon: video,
          icon2x: video2x,
          title: 'campaign.buttons.adTypeComboTitle',
          description: 'campaign.buttons.adTypeComboDesc'
        };
      case AdType.CTV:
        return {
          icon: video,
          icon2x: video2x,
          title: 'campaign.buttons.adTypeCTVTitle',
          description: 'campaign.buttons.adTypeCTVDesc'
        };
      default:
        return this.getDefaultOutdoorAdTypeButtonData(adTypeSetting.name);
    }
  }

  showChangeAdTypeAlertModal = (confirmFuc: () => Promise<void>) => {
    this.changeAdTypeAlertModal = {
      title: i18n.t('common.warning'),
      message: i18n.t('campaign.descriptions.adTypeChangeAlert'),
      primaryBtn: {
        title: i18n.t('common.buttons.ok'),
        callback: async () => {
          await confirmFuc();
          this.hideModal();
        }
      },
      secondaryBtn: {
        title: i18n.t('common.buttons.cancel'),
        callback: this.hideModal
      }
    };
    this.updateState();
  };

  hideModal = () => {
    this.changeAdTypeAlertModal = undefined;
    this.updateState();
  };

  backToL1ObjectDetail = () => {
    this.flowModel.setFinishedRedirectPath(
      `/orders/${this.flowModel.order.orderNumber}/campaign-groups/${this.flowModel.l1Object.l1ObjectId}`
    );
  };

  updateState() {
    this.event.fireEvent(this);
  }
}
